import { useEffect, useMemo, useRef, useState } from 'react';
import { ManageLayout } from '../../components/ManageLayout';
import css from './Exercises.module.css';

interface Alias {
  id: number;
  alias: string;
}

interface MuscleLink {
  muscle_id: string;
  is_primary: number;
}

interface Exercise {
  id: string;
  name: string;
  source: string;
  region: string | null;
  primary_mover: string | null;
  bar_kg: number | null;
  load_note: string | null;
  starting_weight_kg: number | null;
  working_sets: number | null;
  rep_target: string | null;
  rest_sec: number | null;
  warmup: string | null;
  cues_json: string | null;
  hidden: number;
  set_count: number;
  day_ref_count: number;
  aliases: Alias[];
  muscles: MuscleLink[];
}

interface Orphan {
  exercise_id: string;
  set_count: number;
}

interface MuscleGroup {
  id: string;
  name: string;
}

type Filter = 'visible' | 'hidden' | 'all';

function sourceBadgeClass(source: string): string {
  const base: string = css.sourceBadge ?? '';
  switch (source) {
    case 'user':
      return `${base} ${css.sourceBadgeUser ?? ''}`;
    case 'gymbook':
      return `${base} ${css.sourceBadgeGymbook ?? ''}`;
    case 'setgraph':
      return `${base} ${css.sourceBadgeSetgraph ?? ''}`;
    default:
      return base;
  }
}

export function ManageExercises() {
  const [exercises, setExercises] = useState<Exercise[] | null>(null);
  const [orphans, setOrphans] = useState<Orphan[] | null>(null);
  const [muscles, setMuscles] = useState<MuscleGroup[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('visible');
  const [createOpen, setCreateOpen] = useState<{ id?: string; name?: string } | null>(null);
  const [mergeOpen, setMergeOpen] = useState<Orphan | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setError(null);
    try {
      const [exRes, orpRes, muRes] = await Promise.all([
        fetch('/api/manage/exercises'),
        fetch('/api/manage/exercises/orphans'),
        fetch('/api/manage/muscles'),
      ]);
      const ex = (await exRes.json()) as { exercises: Exercise[] };
      const orp = (await orpRes.json()) as { orphans: Orphan[] };
      const mu = (await muRes.json()) as { groups: MuscleGroup[] };
      setExercises(ex.exercises);
      setOrphans(orp.orphans);
      setMuscles(mu.groups);
    } catch {
      setError("couldn't load exercises");
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const filtered = useMemo(() => {
    if (!exercises) return [];
    const q = query.trim().toLowerCase();
    return exercises.filter((e) => {
      if (filter === 'visible' && e.hidden) return false;
      if (filter === 'hidden' && !e.hidden) return false;
      if (!q) return true;
      return (
        e.name.toLowerCase().includes(q) ||
        e.id.toLowerCase().includes(q) ||
        e.aliases.some((a) => a.alias.toLowerCase().includes(q))
      );
    });
  }, [exercises, query, filter]);

  // Group filtered exercises by region. Empty/null region → "(no region)"
  // bucket pushed to the bottom; otherwise regions in alphabetical order.
  const grouped = useMemo(() => {
    const m = new Map<string, Exercise[]>();
    for (const ex of filtered) {
      const key = ex.region?.trim() || '(no region)';
      let list = m.get(key);
      if (!list) {
        list = [];
        m.set(key, list);
      }
      list.push(ex);
    }
    for (const list of m.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return [...m.entries()].sort(([a], [b]) => {
      if (a === '(no region)') return 1;
      if (b === '(no region)') return -1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  return (
    <ManageLayout title="Exercises">
      {error && <div className={css.error}>{error}</div>}
      <div className={css.toolbar}>
        <input
          type="search"
          placeholder="search by name, id, or alias…"
          className={css.search}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className={css.toolbarRight}>
          {(['visible', 'hidden', 'all'] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              className={filter === f ? `${css.filter} ${css.filterActive}` : css.filter}
              onClick={() => setFilter(f)}
            >
              {f}
            </button>
          ))}
          <button type="button" className={css.btn} onClick={() => setCreateOpen({})}>
            new exercise
          </button>
        </div>
      </div>

      {!exercises ? (
        <div className={css.empty}>loading…</div>
      ) : filtered.length === 0 ? (
        <div className={css.empty}>no exercises match</div>
      ) : (
        <div>
          {grouped.map(([region, items]) => (
            <div key={region} className={css.regionGroup}>
              <h3 className={css.regionHeader}>
                {region}
                <span className={css.regionCount}>{items.length}</span>
              </h3>
              <ul className={css.list}>
                {items.map((ex) => (
                  <li
                    key={ex.id}
                    className={`${css.item}${ex.hidden ? ` ${css.itemHidden}` : ''}`}
                  >
                    <div
                      className={css.row}
                      role="button"
                      tabIndex={0}
                      onClick={() => setOpenId(openId === ex.id ? null : ex.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                          setOpenId(openId === ex.id ? null : ex.id);
                      }}
                    >
                      <div className={css.name}>
                        {ex.name}
                        <span className={css.idHint}>{ex.id}</span>
                        <span className={sourceBadgeClass(ex.source)}>{ex.source}</span>
                        {ex.hidden ? <span className={css.sourceBadge}>hidden</span> : null}
                      </div>
                      <div className={css.col}>{ex.set_count} sets</div>
                      <div className={css.col}>{ex.day_ref_count} day{ex.day_ref_count === 1 ? '' : 's'}</div>
                      <div className={css.col}>{ex.aliases.length} alias{ex.aliases.length === 1 ? '' : 'es'}</div>
                      <div className={css.caret}>{openId === ex.id ? '▴' : '▾'}</div>
                    </div>
                    {openId === ex.id && (
                      <ExerciseDetail
                        exercise={ex}
                        muscles={muscles ?? []}
                        onSaved={() => void reload()}
                        onClose={() => setOpenId(null)}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {orphans && orphans.length > 0 && (
        <div className={css.section}>
          <h2 className={css.sectionTitle}>Orphan IDs ({orphans.length})</h2>
          <p style={{ color: '#888', fontSize: 12, margin: '0 0 8px' }}>
            Exercise IDs that have logged sets but no row in <code>gym_exercises</code>. Click promote
            to convert into a defined exercise (the historical sets keep referencing the same id).
          </p>
          {orphans.map((o) => (
            <div key={o.exercise_id} className={css.orphanRow}>
              <span className={css.orphanId}>{o.exercise_id}</span>
              <span className={css.col}>{o.set_count} sets</span>
              <button
                type="button"
                className={css.btn}
                onClick={() => setCreateOpen({ id: o.exercise_id, name: o.exercise_id })}
              >
                promote
              </button>
              <button
                type="button"
                className={css.btnGhost}
                onClick={() => setMergeOpen(o)}
              >
                merge into…
              </button>
            </div>
          ))}
        </div>
      )}

      {createOpen && (
        <CreateExerciseModal
          initialId={createOpen.id}
          initialName={createOpen.name}
          muscles={muscles ?? []}
          onClose={() => setCreateOpen(null)}
          onCreated={() => {
            setCreateOpen(null);
            void reload();
          }}
        />
      )}

      {mergeOpen && (
        <MergeOrphanModal
          orphan={mergeOpen}
          candidates={exercises ?? []}
          onClose={() => setMergeOpen(null)}
          onMerged={() => {
            setMergeOpen(null);
            void reload();
          }}
        />
      )}
    </ManageLayout>
  );
}

// --------------------------------------------------------------------------

function ExerciseDetail({
  exercise,
  muscles,
  onSaved,
  onClose,
}: {
  exercise: Exercise;
  muscles: MuscleGroup[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState({
    name: exercise.name,
    primary_mover: exercise.primary_mover ?? '',
    bar_kg: exercise.bar_kg ?? '',
    load_note: exercise.load_note ?? '',
    starting_weight_kg: exercise.starting_weight_kg ?? '',
    working_sets: exercise.working_sets ?? '',
    rep_target: exercise.rep_target ?? '',
    rest_sec: exercise.rest_sec ?? '',
    warmup: exercise.warmup ?? '',
    cues: parseCues(exercise.cues_json).join('\n'),
  });
  const [aliases, setAliases] = useState(exercise.aliases);
  const [newAlias, setNewAlias] = useState('');
  const [exMuscles, setExMuscles] = useState<Map<string, number>>(
    () => new Map(exercise.muscles.map((m) => [m.muscle_id, m.is_primary])),
  );
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const savedTimer = useRef<number | null>(null);

  // Dirty check — compare every form field against the original prop and the
  // muscle map against the original tags. Normalises empty strings to null so
  // "" and null read as equal.
  const isDirty = useMemo(() => {
    const norm = (v: string): string => v.trim();
    const normOrNull = (v: string): string | null => {
      const t = v.trim();
      return t === '' ? null : t;
    };
    const numOrNull = (v: string | number): number | null => {
      if (typeof v === 'number') return Number.isFinite(v) ? v : null;
      const t = String(v).trim();
      if (t === '') return null;
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    };

    if (norm(form.name) !== exercise.name) return true;
    if (normOrNull(form.primary_mover) !== exercise.primary_mover) return true;
    if (numOrNull(form.bar_kg) !== exercise.bar_kg) return true;
    if (normOrNull(form.load_note) !== exercise.load_note) return true;
    if (numOrNull(form.starting_weight_kg) !== exercise.starting_weight_kg) return true;
    if (numOrNull(form.working_sets) !== exercise.working_sets) return true;
    if (normOrNull(form.rep_target) !== exercise.rep_target) return true;
    if (numOrNull(form.rest_sec) !== exercise.rest_sec) return true;
    if (normOrNull(form.warmup) !== exercise.warmup) return true;

    const cuesNow = form.cues
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    const cuesBefore = parseCues(exercise.cues_json);
    if (cuesNow.length !== cuesBefore.length) return true;
    for (let i = 0; i < cuesNow.length; i++) {
      if (cuesNow[i] !== cuesBefore[i]) return true;
    }

    if (exMuscles.size !== exercise.muscles.length) return true;
    for (const m of exercise.muscles) {
      const v = exMuscles.get(m.muscle_id);
      if (v === undefined || v !== m.is_primary) return true;
    }
    return false;
  }, [form, exMuscles, exercise]);

  useEffect(() => {
    return () => {
      if (savedTimer.current != null) window.clearTimeout(savedTimer.current);
    };
  }, []);

  const refCount = exercise.set_count + exercise.day_ref_count;
  const canHide = refCount === 0 && !exercise.hidden;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const cues = form.cues
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);

      // 1. Patch fields
      const fieldsRes = await fetch(`/api/manage/exercises/${exercise.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          primary_mover: emptyToNull(form.primary_mover),
          bar_kg: emptyToNumber(form.bar_kg),
          load_note: emptyToNull(form.load_note),
          starting_weight_kg: emptyToNumber(form.starting_weight_kg),
          working_sets: emptyToNumber(form.working_sets),
          rep_target: emptyToNull(form.rep_target),
          rest_sec: emptyToNumber(form.rest_sec),
          warmup: emptyToNull(form.warmup),
          cues: cues.length ? cues : null,
        }),
      });
      if (!fieldsRes.ok) throw new Error(`patch failed: ${fieldsRes.status}`);

      // 2. Diff muscles → bulk-set
      const before = new Map(exercise.muscles.map((m) => [m.muscle_id, m.is_primary]));
      const adds: { muscle_id: string; is_primary: boolean }[] = [];
      const removes: string[] = [];
      for (const [mid, isPrim] of exMuscles) {
        const prev = before.get(mid);
        if (prev === undefined || prev !== isPrim) {
          adds.push({ muscle_id: mid, is_primary: isPrim === 1 });
        }
      }
      for (const mid of before.keys()) if (!exMuscles.has(mid)) removes.push(mid);
      if (adds.length || removes.length) {
        const muRes = await fetch(`/api/manage/exercises/${exercise.id}/muscles`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ adds, removes }),
        });
        if (!muRes.ok) throw new Error(`muscles failed: ${muRes.status}`);
      }
      // Flash "saved" before the reload swaps the underlying exercise prop.
      setSavedAt(Date.now());
      if (savedTimer.current != null) window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setSavedAt(null), 2000);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  async function addAlias() {
    if (!newAlias.trim()) return;
    setError(null);
    try {
      const res = await fetch('/api/manage/aliases', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ exercise_id: exercise.id, alias: newAlias.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error ?? `add failed: ${res.status}`);
      setAliases((prev) => [...prev, { id: data.id, alias: data.alias }]);
      setNewAlias('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'alias add failed');
    }
  }

  async function removeAlias(id: number) {
    setError(null);
    try {
      const res = await fetch(`/api/manage/aliases/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`remove failed: ${res.status}`);
      setAliases((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'alias remove failed');
    }
  }

  async function hide() {
    if (!canHide) return;
    setError(null);
    try {
      const res = await fetch(`/api/manage/exercises/${exercise.id}/hide`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error ?? `hide failed: ${res.status}`);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'hide failed');
    }
  }

  async function unhide() {
    setError(null);
    try {
      const res = await fetch(`/api/manage/exercises/${exercise.id}/unhide`, { method: 'POST' });
      if (!res.ok) throw new Error(`unhide failed: ${res.status}`);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unhide failed');
    }
  }

  function toggleMuscle(id: string, target: 'primary' | 'secondary' | 'none') {
    setExMuscles((prev) => {
      const next = new Map(prev);
      if (target === 'none') next.delete(id);
      else next.set(id, target === 'primary' ? 1 : 0);
      return next;
    });
  }

  return (
    <div className={css.detail}>
      {error && <div className={css.error}>{error}</div>}

      <div className={css.fieldGrid}>
        <Field
          label="name"
          value={form.name}
          onChange={(v) => setForm({ ...form, name: v })}
        />
        <div className={css.field}>
          <label className={css.label}>region</label>
          <div className={css.derived}>
            <span>{exercise.region ?? '—'}</span>
            <span className={css.derivedHint}>derived from primary muscle tags</span>
          </div>
        </div>
        <Field
          label="primary mover"
          value={form.primary_mover}
          onChange={(v) => setForm({ ...form, primary_mover: v })}
        />
        <Field
          label="bar kg (0 = sled, 20 = olympic, 25 = trap)"
          value={String(form.bar_kg)}
          type="number"
          onChange={(v) => setForm({ ...form, bar_kg: v })}
        />
        <Field
          label="starting weight (kg)"
          value={String(form.starting_weight_kg)}
          type="number"
          onChange={(v) => setForm({ ...form, starting_weight_kg: v })}
        />
        <Field
          label="working sets"
          value={String(form.working_sets)}
          type="number"
          onChange={(v) => setForm({ ...form, working_sets: v })}
        />
        <Field
          label="rep target (e.g. 8-10, 10/leg, 15 min)"
          value={form.rep_target}
          onChange={(v) => setForm({ ...form, rep_target: v })}
        />
        <Field
          label="rest (sec)"
          value={String(form.rest_sec)}
          type="number"
          onChange={(v) => setForm({ ...form, rest_sec: v })}
        />
        <Field
          label="load note (e.g. per dumbbell)"
          value={form.load_note}
          fullWidth
          onChange={(v) => setForm({ ...form, load_note: v })}
        />
        <Field
          label="warmup protocol"
          value={form.warmup}
          fullWidth
          textarea
          onChange={(v) => setForm({ ...form, warmup: v })}
        />
        <Field
          label="cues (one per line)"
          value={form.cues}
          fullWidth
          textarea
          onChange={(v) => setForm({ ...form, cues: v })}
        />
      </div>

      <div className={css.tagsBlock}>
        <div className={css.tagsHeader}>
          <span className={css.tagsTitle}>Aliases</span>
        </div>
        <div className={css.aliasList}>
          {aliases.map((a) => (
            <span key={a.id} className={css.alias}>
              {a.alias}
              <button
                type="button"
                className={css.aliasRemove}
                onClick={() => void removeAlias(a.id)}
                aria-label={`remove alias ${a.alias}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className={css.aliasInput}>
          <input
            type="text"
            placeholder="add alias (e.g. machine-leg-presses)"
            value={newAlias}
            onChange={(e) => setNewAlias(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void addAlias();
              }
            }}
          />
          <button type="button" className={css.btnGhost} onClick={() => void addAlias()}>
            add
          </button>
        </div>
      </div>

      <div className={css.tagsBlock}>
        <div className={css.tagsHeader}>
          <span className={css.tagsTitle}>Muscles</span>
        </div>
        <div className={css.muscleGrid}>
          {muscles.map((g) => {
            const tier = exMuscles.get(g.id);
            return (
              <div key={g.id} className={css.muscleCell}>
                <span className={css.muscleName}>{g.name}</span>
                <span className={css.muscleTier}>
                  <button
                    type="button"
                    onClick={() => toggleMuscle(g.id, tier === 1 ? 'none' : 'primary')}
                    className={tier === 1 ? css.tierActive : ''}
                    aria-label={`mark ${g.name} as primary`}
                  >
                    P
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleMuscle(g.id, tier === 0 ? 'none' : 'secondary')}
                    className={tier === 0 ? css.tierActive : ''}
                    aria-label={`mark ${g.name} as secondary`}
                  >
                    S
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className={css.actions}>
        <span className={css.refsHint}>
          {refCount === 0
            ? 'no references — can be hidden'
            : `${exercise.set_count} sets · ${exercise.day_ref_count} day refs · cannot hide`}
        </span>
        {exercise.hidden ? (
          <button type="button" className={css.btnGhost} onClick={() => void unhide()}>
            unhide
          </button>
        ) : (
          <button
            type="button"
            className={css.btnDanger}
            onClick={() => void hide()}
            disabled={!canHide}
            title={canHide ? undefined : 'has references — cannot hide'}
          >
            delete (hide)
          </button>
        )}
        <button type="button" className={css.btnGhost} onClick={onClose}>
          close
        </button>
        <button
          type="button"
          className={css.btn}
          onClick={() => void save()}
          disabled={saving || !isDirty}
          title={!isDirty && !saving ? 'no changes to save' : undefined}
        >
          {saving ? 'saving…' : savedAt ? 'saved ✓' : isDirty ? 'save' : 'saved'}
        </button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  textarea = false,
  fullWidth = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  textarea?: boolean;
  fullWidth?: boolean;
}) {
  return (
    <div className={`${css.field}${fullWidth ? ` ${css.fieldFull}` : ''}`}>
      <label className={css.label}>{label}</label>
      {textarea ? (
        <textarea
          className={css.textarea}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          type={type}
          className={css.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

function emptyToNull(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}
function emptyToNumber(s: string | number): number | null {
  if (typeof s === 'number') return Number.isFinite(s) ? s : null;
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}
function parseCues(json: string | null): string[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

// --------------------------------------------------------------------------

function CreateExerciseModal({
  initialId,
  initialName,
  muscles,
  onClose,
  onCreated,
}: {
  initialId?: string;
  initialName?: string;
  muscles: MuscleGroup[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState(initialName ?? '');
  const [id, setId] = useState(initialId ?? '');
  const [idTouched, setIdTouched] = useState(!!initialId);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pickedMuscles, setPickedMuscles] = useState<Map<string, number>>(new Map());

  function nameToId(s: string): string {
    return s
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  async function submit() {
    setError(null);
    setSubmitting(true);
    try {
      const adds = [...pickedMuscles.entries()].map(([muscle_id, t]) => ({
        muscle_id,
        is_primary: t === 1,
      }));
      const res = await fetch('/api/manage/exercises', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          id: id || undefined,
          name,
          muscles: adds,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error ?? `create failed: ${res.status}`);
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'create failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={css.modal} onClick={onClose}>
      <div className={css.modalCard} onClick={(e) => e.stopPropagation()}>
        <h2 className={css.modalTitle}>{initialId ? 'Promote to defined exercise' : 'New exercise'}</h2>
        {error && <div className={css.error}>{error}</div>}
        <div className={css.fieldGrid}>
          <Field
            label="name"
            value={name}
            fullWidth
            onChange={(v) => {
              setName(v);
              if (!idTouched) setId(nameToId(v));
            }}
          />
          <Field
            label="id (slug)"
            value={id}
            fullWidth
            onChange={(v) => {
              setId(v);
              setIdTouched(true);
            }}
          />
        </div>
        <p style={{ color: '#888', fontSize: 12, marginTop: 0 }}>
          You can edit weights, sets, cues, aliases, and muscles after creation. Tag primary/secondary
          muscles now if you like:
        </p>
        <div className={css.muscleGrid}>
          {muscles.map((g) => {
            const tier = pickedMuscles.get(g.id);
            return (
              <div key={g.id} className={css.muscleCell}>
                <span className={css.muscleName}>{g.name}</span>
                <span className={css.muscleTier}>
                  <button
                    type="button"
                    onClick={() => {
                      setPickedMuscles((prev) => {
                        const next = new Map(prev);
                        if (tier === 1) next.delete(g.id);
                        else next.set(g.id, 1);
                        return next;
                      });
                    }}
                    className={tier === 1 ? css.tierActive : ''}
                  >
                    P
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPickedMuscles((prev) => {
                        const next = new Map(prev);
                        if (tier === 0) next.delete(g.id);
                        else next.set(g.id, 0);
                        return next;
                      });
                    }}
                    className={tier === 0 ? css.tierActive : ''}
                  >
                    S
                  </button>
                </span>
              </div>
            );
          })}
        </div>
        <div className={css.actions}>
          <button type="button" className={css.btnGhost} onClick={onClose}>
            cancel
          </button>
          <button
            type="button"
            className={css.btn}
            onClick={() => void submit()}
            disabled={submitting || !name.trim()}
          >
            {submitting ? 'creating…' : 'create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MergeOrphanModal({
  orphan,
  candidates,
  onClose,
  onMerged,
}: {
  orphan: { exercise_id: string; set_count: number };
  candidates: Exercise[];
  onClose: () => void;
  onMerged: () => void;
}) {
  const [query, setQuery] = useState('');
  const [targetId, setTargetId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter candidates by name / id substring. Hidden exercises are
  // shown too (you might be merging into something you previously hid).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates.slice(0, 30);
    return candidates
      .filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.id.toLowerCase().includes(q) ||
          e.aliases.some((a) => a.alias.toLowerCase().includes(q)),
      )
      .slice(0, 30);
  }, [candidates, query]);

  async function submit() {
    if (!targetId) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(
        `/api/manage/exercises/orphans/${encodeURIComponent(orphan.exercise_id)}/merge-into`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ targetId }),
        },
      );
      const data = (await res.json().catch(() => null)) as
        | { error?: string }
        | null;
      if (!res.ok) {
        setError(data?.error ?? `merge failed: ${res.status}`);
        setSubmitting(false);
        return;
      }
      onMerged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'merge failed');
      setSubmitting(false);
    }
  }

  const target = targetId ? candidates.find((e) => e.id === targetId) ?? null : null;

  return (
    <div className={css.modal} onClick={onClose}>
      <div className={css.modalCard} onClick={(e) => e.stopPropagation()}>
        <h2 className={css.modalTitle}>Merge orphan into existing exercise</h2>
        <p style={{ color: '#888', fontSize: 13, margin: '0 0 12px' }}>
          All <strong style={{ color: '#ddd' }}>{orphan.set_count}</strong> set
          {orphan.set_count === 1 ? '' : 's'} logged under{' '}
          <code style={{ color: '#ddd' }}>{orphan.exercise_id}</code> will be
          repointed at the target. The orphan id stays behind as an alias on
          the target so future voice/import paths route correctly.
        </p>
        {error && <div className={css.error}>{error}</div>}

        <input
          type="search"
          placeholder="search exercises…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={css.input}
          autoFocus
          style={{ marginBottom: 8 }}
        />

        <div
          style={{
            maxHeight: 280,
            overflowY: 'auto',
            border: '1px solid #2a2a2a',
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ color: '#666', fontSize: 13, padding: 12, textAlign: 'center' }}>
              no matches
            </div>
          ) : (
            filtered.map((e) => {
              const selected = e.id === targetId;
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setTargetId(e.id)}
                  className={css.orphanRow}
                  style={{
                    width: '100%',
                    background: selected ? '#1f1f1f' : 'transparent',
                    border: 'none',
                    borderBottom: '1px solid #1a1a1a',
                    color: '#e5e5e5',
                    fontFamily: 'inherit',
                    textAlign: 'left',
                    cursor: 'pointer',
                    padding: '8px 12px',
                  }}
                >
                  <span style={{ flex: 1 }}>
                    {e.name}
                    {e.hidden ? ' (hidden)' : ''}
                  </span>
                  <span style={{ color: '#666', fontSize: 11, fontFamily: 'monospace' }}>
                    {e.id}
                  </span>
                  <span className={css.col}>{e.set_count} sets</span>
                </button>
              );
            })
          )}
        </div>

        {target && (
          <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 12px' }}>
            About to move <strong>{orphan.set_count}</strong> set
            {orphan.set_count === 1 ? '' : 's'} from{' '}
            <code>{orphan.exercise_id}</code> into{' '}
            <strong style={{ color: '#ddd' }}>{target.name}</strong>.
          </p>
        )}

        <div className={css.actions}>
          <button type="button" className={css.btnGhost} onClick={onClose}>
            cancel
          </button>
          <button
            type="button"
            className={css.btn}
            onClick={() => void submit()}
            disabled={submitting || !targetId}
          >
            {submitting ? 'merging…' : 'merge'}
          </button>
        </div>
      </div>
    </div>
  );
}
